import { interestedPartiesLeaderRoleByProcess } from "../constants/enums";
import { hasEffectiveRole, isRoleUtil, normalizeRoleId } from "./roleAliases";

/** Administrador total del módulo: INTERESTED_PARTIES_MODULE_ADMIN */
export const INTERESTED_PARTIES_MODULE_ADMIN_ROLE =
  "INTERESTED_PARTIES_MODULE_ADMIN";

/** Solo consulta en el proceso: INTERESTED_PARTIES_COLLABORATOR (sin permisos de líder/admin en ese proceso). */
export const INTERESTED_PARTIES_COLLABORATOR_ROLE =
  "INTERESTED_PARTIES_COLLABORATOR";

const hasExactRole = (roleList = [], roleId) => {
  const target = normalizeRoleId(roleId);
  return (roleList || []).some((role) => normalizeRoleId(role) === target);
};

// --- Módulo completo (pestañas externas/internas, fechas del documento) ---

export function isInterestedPartiesModuleAdmin(roleList) {
  return (
    Array.isArray(roleList) &&
    hasEffectiveRole(roleList, INTERESTED_PARTIES_MODULE_ADMIN_ROLE)
  );
}

// --- Por proceso (detalle por proceso, crear/editar registro, historial) ---

/**
 * Roles de líder del proceso en BD: SERALOGIN_PROCESS_LEADER_*.
 * No incluye *_MANAGEMENT_LEADER legacy: el backend solo acepta SERALOGIN_PROCESS_LEADER_*.
 */
export function getInterestedPartiesEditableLeaderRoles(processId) {
  const id = Number(processId);
  if (!Number.isFinite(id)) return [];
  const moduleRole = interestedPartiesLeaderRoleByProcess[id];
  return moduleRole ? [moduleRole] : [];
}

export function getInterestedPartiesLeaderRoleForProcess(processId) {
  const id = Number(processId);
  if (!Number.isFinite(id)) return null;
  return interestedPartiesLeaderRoleByProcess[id] ?? null;
}

/** Conjunto de todos los nombres de rol de líder definidos por proceso. */
function getAllInterestedPartiesLeaderRoleNames() {
  const s = new Set();
  Object.values(interestedPartiesLeaderRoleByProcess).forEach((r) => {
    if (r) s.add(r);
  });
  return s;
}

/**
 * Tiene al menos un rol de líder de partes interesadas (cualquier proceso).
 * Sirve para permitir ver historiales en todos los procesos sin poder mutar fuera del propio.
 */
export function hasAnyInterestedPartiesLeaderRole(roleList) {
  if (!Array.isArray(roleList)) return false;
  for (const r of getAllInterestedPartiesLeaderRoleNames()) {
    if (hasExactRole(roleList, r)) return true;
  }
  return false;
}

/**
 * Admin del módulo o líder del proceso: puede crear, editar y eliminar registros de ese proceso.
 * Solo SERALOGIN_PROCESS_LEADER_* (sin alias legacy *_MANAGEMENT_LEADER).
 */
export function canManageInterestedPartiesProcessRecords(roleList, processId) {
  if (!roleList || !Array.isArray(roleList)) return false;
  if (isInterestedPartiesModuleAdmin(roleList)) return true;
  const leaderRoles = getInterestedPartiesEditableLeaderRoles(processId);
  return leaderRoles.some((r) => hasExactRole(roleList, r));
}

/** @deprecated Usar canManageInterestedPartiesProcessRecords */
export function canEditInterestedPartiesByProcess(roleList, processId) {
  return canManageInterestedPartiesProcessRecords(roleList, processId);
}

/**
 * Colaborador explícito en roleList (puede convivir con otros roles; si además es líder/admin del proceso,
 * manda canManageInterestedPartiesProcessRecords).
 */
export function hasInterestedPartiesCollaboratorRole(roleList) {
  return (
    Array.isArray(roleList) &&
    isRoleUtil(INTERESTED_PARTIES_COLLABORATOR_ROLE, roleList)
  );
}

/**
 * Solo lectura en el detalle del proceso: no es admin ni líder de ese proceso.
 */
export function isInterestedPartiesProcessReadOnly(roleList, processId) {
  return !canManageInterestedPartiesProcessRecords(roleList, processId);
}

/**
 * Ver historiales en cualquier proceso: admin del módulo o cualquier líder SERALOGIN_PROCESS_LEADER_*.
 */
export function canViewInterestedPartiesProcessHistory(roleList) {
  if (!roleList || !Array.isArray(roleList)) return false;
  if (isInterestedPartiesModuleAdmin(roleList)) return true;
  return hasAnyInterestedPartiesLeaderRole(roleList);
}
